import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Common/Navbar";
import "./ProfileSettings.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(
  /\/+$/,
  ""
);

const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "isLoggedIn",
  "user",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

const getToken = () => {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    ""
  ).trim();
};

const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  window.dispatchEvent(new Event("auth-state-changed"));
};

const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : {};
  } catch {
    return {};
  }
};

const formatMemberSince = (value) => {
  if (!value) return "2026";

  try {
    return new Date(value).getFullYear();
  } catch {
    return value;
  }
};

const plans = [
  {
    key: "standard",
    title: "Standard Plan",
    price: "₹49",
    rawAmount: 49,
    duration: "Monthly",
    features: [
      "Basic hosting support",
      "Standard account protection",
      "Email assistance included",
    ],
  },
  {
    key: "business",
    title: "Business Plan",
    price: "₹79",
    rawAmount: 79,
    duration: "Monthly",
    features: [
      "Priority support",
      "Better performance tools",
      "Advanced business features",
    ],
  },
  {
    key: "premium",
    title: "Premium Plan",
    price: "₹99",
    rawAmount: 99,
    duration: "Monthly",
    features: [
      "Premium support",
      "Maximum account coverage",
      "Priority service and setup help",
    ],
  },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const user = useMemo(() => getStoredUser(), []);

  const fullName =
    user?.fullName ||
    user?.name ||
    user?.realName ||
    user?.username ||
    localStorage.getItem("fullName") ||
    localStorage.getItem("name") ||
    "User Name";

  const email =
    user?.email ||
    localStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    "user@example.com";

  const phone = user?.phone || "+91 00000 00000";
  const location = user?.location || "Mumbai, India";
  const joinDate = formatMemberSince(user?.createdAt || user?.created_at);
  const accountPlan = user?.plan || "Standard";
  const accountStatus = user?.status || "Active";

  const avatar =
    user?.profileImage ||
    user?.profilePhoto ||
    user?.photoURL ||
    user?.avatar ||
    "";

  const firstLetter = String(fullName || "U").charAt(0).toUpperCase();

  const selectedPlanData = useMemo(() => {
    return plans.find((plan) => plan.key === selectedPlan) || plans[0];
  }, [selectedPlan]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditProfile = () => {
    setNotice("Profile edit option will be connected with backend profile update API.");
    setError("");
  };

  const handlePayment = async () => {
    try {
      setPaymentLoading(true);
      setError("");
      setNotice("");

      const token = getToken();

      if (!token) {
        clearAuthStorage();
        navigate("/signin", { replace: true });
        return;
      }

      const response = await fetch(`${API_BASE}/api/payments/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName,
          email,
          planName: selectedPlanData.title,
          amount: selectedPlanData.rawAmount,
          paymentMethod: "Online",
          status: "success",
          paymentId: `LOCAL-${Date.now()}`,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        clearAuthStorage();
        navigate("/signin", { replace: true });
        return;
      }

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to process payment right now.");
      }

      setNotice(`Payment saved successfully for ${selectedPlanData.title}.`);
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "Unable to process payment right now.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleViewPaymentHistory = () => {
    navigate("/payment-history");
  };

  return (
    <>
      <Navbar />

      <section className="ps-page">
        <div className="ps-container">
          <div className="ps-topbar">
            <button
              type="button"
              className="ps-back-btn"
              onClick={handleBack}
              aria-label="Go back"
            >
              ← Back
            </button>
          </div>

          {error && <div className="auth-alert auth-error">{error}</div>}
          {notice && <div className="auth-alert auth-success">{notice}</div>}

          <div className="ps-header-card">
            <div className="ps-avatar-wrap">
              {avatar ? (
                <img
                  src={avatar}
                  alt={`${fullName} profile`}
                  className="ps-avatar-img"
                />
              ) : (
                <div className="ps-avatar-fallback">{firstLetter}</div>
              )}
            </div>

            <div className="ps-header-content">
              <span className="ps-badge">My Profile</span>
              <h1>{fullName}</h1>
              <p>
                Manage your personal information, account details, and payment plans.
              </p>
            </div>

            <div className="ps-header-action">
              <button
                type="button"
                className="ps-edit-btn"
                onClick={handleEditProfile}
              >
                Edit Profile
              </button>
            </div>
          </div>

          <div className="ps-grid">
            <div className="ps-card">
              <h3>Personal Information</h3>

              <div className="ps-info-list">
                <div className="ps-info-item">
                  <span>Full Name</span>
                  <strong>{fullName}</strong>
                </div>

                <div className="ps-info-item">
                  <span>Email Address</span>
                  <strong>{email}</strong>
                </div>

                <div className="ps-info-item">
                  <span>Phone Number</span>
                  <strong>{phone}</strong>
                </div>

                <div className="ps-info-item">
                  <span>Location</span>
                  <strong>{location}</strong>
                </div>
              </div>
            </div>

            <div className="ps-card">
              <h3>Account Overview</h3>

              <div className="ps-stats-grid">
                <div className="ps-stat-box">
                  <span>Account Status</span>
                  <strong>{accountStatus}</strong>
                </div>

                <div className="ps-stat-box">
                  <span>Member Since</span>
                  <strong>{joinDate}</strong>
                </div>

                <div className="ps-stat-box">
                  <span>Plan</span>
                  <strong>{accountPlan}</strong>
                </div>

                <div className="ps-stat-box">
                  <span>Verification</span>
                  <strong>Verified</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="ps-card ps-payment-card">
            <div className="ps-payment-header">
              <div>
                <h3>Payment Section</h3>
                <p className="ps-payment-subtext">
                  Select a plan and continue your payment process from here.
                </p>
              </div>

              <button
                type="button"
                className="ps-history-btn"
                onClick={handleViewPaymentHistory}
              >
                Payment History
              </button>
            </div>

            <div className="ps-plan-grid">
              {plans.map((plan) => (
                <button
                  type="button"
                  key={plan.key}
                  className={`ps-plan-card ${
                    selectedPlan === plan.key ? "active" : ""
                  }`}
                  onClick={() => setSelectedPlan(plan.key)}
                >
                  <div className="ps-plan-top">
                    <h4>{plan.title}</h4>
                    <div className="ps-plan-price">{plan.price}</div>
                    <span className="ps-plan-duration">{plan.duration}</span>
                  </div>

                  <ul className="ps-plan-features">
                    {plan.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>

                  <span className="ps-select-plan-btn">
                    {selectedPlan === plan.key ? "Selected" : "Select Plan"}
                  </span>
                </button>
              ))}
            </div>

            <div className="ps-payment-action-row">
              <div className="ps-selected-plan-box">
                <span>Selected Plan</span>
                <strong>{selectedPlanData.title}</strong>
              </div>

              <button
                type="button"
                className="ps-pay-btn"
                onClick={handlePayment}
                disabled={paymentLoading}
              >
                {paymentLoading ? "Processing..." : "Pay Now"}
              </button>
            </div>
          </div>

          <div className="ps-card">
            <h3>About Account</h3>
            <p className="ps-about-text">
              This section shows your account profile details from the current secure
              session. Payment actions are connected with your backend API and your
              payment history remains private to your logged-in account.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProfilePage;